import * as RadixRadioGroup from '@radix-ui/react-radio-group';
import { useTranslation } from 'react-i18next';

import { DASHBOARD_PERIODS, type DashboardPeriod } from '@/api/dashboard';
import { focusRing } from '@/components/ui/controlStyles';
import { cn } from '@/lib/cn';

interface Props {
  value: DashboardPeriod;
  onChange: (period: DashboardPeriod) => void;
}

/**
 * `DESIGN.md` §6.2's header control — `Last 30 days` / `Last 90 days` / `All time`, default 30, as a
 * **segmented control with all three options visible at once**: the selected one filled in the tenant
 * accent with `--accent-fg` text, the other two on `paper` behind a `rule-control` hairline (§3: the
 * border of an interactive control is never the decorative `rule`).
 *
 * ⚠️ **It was a Radix `Select` on a mid-page *Analysis* band until 2026-08-12, and both halves are
 * `reversed`** (TASK-148 decided it, TASK-149 built it; TASK-127..129's arguments were put to the user
 * on 2026-08-12 and the user chose the original design). A closed combobox shows one option and hides
 * the fact that there are three; the reference shows the whole choice, which is why the control has
 * moved back beside the title and changed shape at the same time.
 *
 * ⚠️ **The control's scope did not move with it**, in either direction: `period` drives the same one
 * request and the same query key, the same sections re-read on a change, and `hasPeriodScopedSection`
 * still decides whether it renders at all — which is what keeps it off an `Interviewer`'s screen. A
 * control that moves and takes its meaning with it is a different feature.
 *
 * ⚠️ **Built on the Radix radio-group primitive here rather than on `components/ui/RadioGroup.tsx`,
 * and that is a scoping decision rather than a duplication.** The shared component is §6.6's score
 * scale — a dot beside a label, with markup a caller cannot restyle into a filled segment — and
 * `components/ui/` is shared by five screens and is not this sprint's to widen. §4's rule settles it:
 * anything used on only one screen belongs in that screen's folder. **No new dependency**:
 * `@radix-ui/react-radio-group` is what the shared component is already built on, so the keyboard
 * behaviour §9 asks for (arrow keys moving between the three, one tab stop, one checked option) is the
 * primitive's rather than this file's.
 *
 * The options come from `DASHBOARD_PERIODS`, whose type is narrowed out of the schema's own query enum
 * (`QueryOf<'/api/v1/dashboard/', 'get'>`): a fourth value is a **422** from the API rather than a
 * silent fall back to the default, so the screen must be unable to invent one.
 */
export function PeriodSelector({ value, onChange }: Props) {
  const { t } = useTranslation('dashboard');

  return (
    <RadixRadioGroup.Root
      aria-label={t('period.label')}
      value={value}
      onValueChange={(next) => onChange(next as DashboardPeriod)}
      orientation="horizontal"
      className="flex items-center"
    >
      {DASHBOARD_PERIODS.map((period) => {
        const selected = period === value;

        return (
          <RadixRadioGroup.Item
            key={period}
            value={period}
            className={cn(
              // The segments share their hairlines, so three controls read as one control.
              '-ml-px border px-3 py-1 text-xs transition-motion first:ml-0 first:rounded-l last:rounded-r',
              selected
                ? 'relative z-10 border-accent bg-accent font-medium text-accent-fg'
                : 'border-rule-control bg-paper text-ink-700 hover:bg-paper-sunken',
              focusRing,
            )}
          >
            {t(`period.${period}`)}
          </RadixRadioGroup.Item>
        );
      })}
    </RadixRadioGroup.Root>
  );
}
